import { Button, Divider, Table, message, Spin } from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ArrowLeftOutlined } from "@ant-design/icons";
import ResponseMessage from "../Notification/ResponseMessge";
import ManageBinService from "../../../services/Dispatch-Centre-Service/ManageBinService";

const tableStyles = `
  .compact-table .ant-table-thead > tr > th {
    padding: 8px 4px;
  }

  .compact-table .ant-table-tbody > tr > td {
    padding: 6px 4px;
  }

  .compact-table .ant-table-row {
    height: 32px;
  }

  .compact-table .color-column {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .confirm-btn-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 24px;
    padding-bottom: 8px;
  }

  .confirm-btn {
    min-width: 220px;
    height: 44px;
    border-radius: 10px;
    font-weight: 600;
  }

  .preview-loader-wrap {
    min-height: 260px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .preview-empty-wrap {
    min-height: 180px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #64748b;
    font-size: 15px;
    font-weight: 500;
  }

  @media (max-width: 768px) {
    .confirm-btn {
      width: 100%;
      max-width: 280px;
    }
  }
`;

const PreviewBulkScan = ({ handleConfirmBinInward, type, title }) => {
  const [formatedBinId, setFormatedBinId] = useState([]);
  const [binData, setBinData] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBinData = async () => {
      try {
        setPageLoading(true);

        const storedBinIds =
          JSON.parse(localStorage.getItem("bin_bulk_outward_inward")) || [];
        console.log("Stored bin IDs:", storedBinIds);

        if (storedBinIds.length === 0) {
          message.warning("No Bin IDs found in local storage.");
          setBinData([]);
          return;
        }

        if (Array.isArray(storedBinIds)) {
          const binIdsArray = storedBinIds.map((bin) => bin.bin_id);
          setFormatedBinId(binIdsArray);

          const response = await ManageBinService.getBulkBinData(binIdsArray);

          if (response?.message && response?.data) {
            const processedData = processBinData(response.data);
            setBinData(processedData);
          } else {
            setBinData([]);
            ResponseMessage.error({
              message: "No response message from the server.",
            });
          }
        } else {
          setBinData([]);
          ResponseMessage.error({
            message: "No valid bin data found.",
          });
        }
      } catch (error) {
        console.error("Error fetching bin data:", error);
        setBinData([]);
        ResponseMessage.error({ message: "Failed to fetch bin data." });
      } finally {
        setPageLoading(false);
      }
    };

    fetchBinData();
  }, []);

  const extractColorName = (color) => {
    if (!color || typeof color !== "string") {
      return "Unknown Color";
    }

    if (color.startsWith("00:")) {
      color = color.replace("00:", "");
    }

    const words = color.split(" ");
    let firstSpecIndex = -1;

    for (let i = 0; i < words.length; i++) {
      if (words[i].includes("/") && /\d/.test(words[i])) {
        firstSpecIndex = i;
        break;
      }
    }

    return firstSpecIndex >= 0
      ? words.slice(0, firstSpecIndex).join(" ").trim()
      : color.trim();
  };

  const processBinData = (data) => {
    const itemMap = new Map();

    data.forEach((bin) => {
      bin.bin_data.forEach(
        ({ item_no, color, kanban_no, size_qty, total_qty }) => {
          const cleanedColor = extractColorName(color);
          const sizesParsed = JSON.parse(size_qty);

          if (!itemMap.has(item_no)) {
            itemMap.set(item_no, new Map());
          }

          const colorMap = itemMap.get(item_no);

          if (!colorMap.has(cleanedColor)) {
            colorMap.set(cleanedColor, {
              sizeQty: {},
              total: 0,
              kanban_nos: [],
            });
          }

          const colorData = colorMap.get(cleanedColor);
          colorData.kanban_nos.push(kanban_no);

          Object.entries(sizesParsed).forEach(([size, qty]) => {
            const numericQty = Number(qty);
            colorData.sizeQty[size] =
              (Number(colorData.sizeQty[size]) || 0) + numericQty;
          });

          colorData.total = Number(colorData.total) + Number(total_qty);
        }
      );
    });

    return Array.from(itemMap, ([item_no, colorMap]) => {
      const availableSizes = new Set();

      const details = Array.from(
        colorMap,
        ([color, { sizeQty, total, kanban_nos }]) => {
          Object.keys(sizeQty).forEach((size) => availableSizes.add(size));

          return {
            color,
            sizeQty,
            total,
            kanban_no: kanban_nos.join(", "),
          };
        }
      );

      return {
        item_no,
        details,
        availableSizes: Array.from(availableSizes),
      };
    });
  };

const handleConfirmBinOutward = async () => {
  const userData = JSON.parse(localStorage.getItem("userData"));
  const addedBy = userData?.data?.su_id;

  try {
    setConfirmLoading(true);

    const response = await handleConfirmBinInward(formatedBinId, addedBy);

    // ✅ check success
    if (response?.status === "success" || response?.status === true) {
      localStorage.removeItem("bin_bulk_outward_inward");

      ResponseMessage.success({
        message: response?.message || "Bins confirmed successfully!",
      });
       navigate(-1);
    } else {
      // ❗ API returned failure
      ResponseMessage.error({
        message: response?.message || "Something went wrong!",
      });
       navigate(-1);
    }

  } catch (error) {
    console.error("Error confirming bins:", error);

    ResponseMessage.error({
      message: error?.message || "Failed to confirm bins. Please try again.",
    });

  } finally {
    setConfirmLoading(false);
  }
};
  const handleArrowClick = () => {
    navigate(-1);
  };

  return (
    <>
      <style>{tableStyles}</style>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <h3 style={{ margin: 0 }}>
          <ArrowLeftOutlined
            onClick={handleArrowClick}
            style={{ paddingRight: "10px", cursor: "pointer" }}
          />
          {title}
        </h3>
      </div>

      <Divider style={{ marginTop: 8 }} />

      {pageLoading ? (
        <div className="preview-loader-wrap">
          <Spin size="large" />
        </div>
      ) : binData.length > 0 ? (
        <>
          {binData.map(({ item_no, details, availableSizes }) => {
            const columns = [
              {
                title: "Color",
                dataIndex: "color",
                key: "color",
                width: 100,
                className: "color-column",
                ellipsis: true,
              },
              ...availableSizes.map((size) => ({
                title: size,
                dataIndex: ["sizeQty", size],
                key: size,
                width: 60,
                align: "center",
                render: (text) => text || "-",
              })),
              {
                title: "Total",
                dataIndex: "total",
                key: "total",
                width: 60,
                align: "center",
              },
            ];

            return (
              <div key={item_no} style={{ marginBottom: 16 }}>
                <h3 style={{ marginBottom: 8 }}>Item: {item_no}</h3>
                <Table
                  columns={columns}
                  dataSource={details}
                  pagination={false}
                  bordered
                  rowKey="color"
                  size="small"
                  className="compact-table"
                  scroll={{ x: "max-content" }}
                />
              </div>
            );
          })}

          <Divider />

          <div className="confirm-btn-wrap">
            <Button
              type="primary"
              danger
              size="large"
              className="confirm-btn"
              loading={confirmLoading}
              disabled={!formatedBinId.length}
              onClick={handleConfirmBinOutward}
            >
              Confirm
            </Button>
          </div>
        </>
      ) : (
        <div className="preview-empty-wrap">No preview data available.</div>
      )}
    </>
  );
};

export default PreviewBulkScan;